import { Component, OnInit } from "@angular/core";
import { KeycloakService } from "./keycloak.service";
import { Router } from "@angular/router";
import { HelperService } from "src/app/shared/services/helper.service";
import { APIRequestService } from "../shared/services/api/apirequest.service";
import { APIURLTypes } from "../shared/enums/apiurltypes.enum";
import { finalize } from "rxjs/operators";

interface DecodedToken {
  permissions?: string[];
  export_report?: boolean;
  [key: string]: any;
}

@Component({
  selector: "app-keycloak-callback",
  templateUrl: "./keycloak-callback.component.html",
  styleUrls: ["./keycloak-callback.component.scss"],
})
export class KeycloakCallbackComponent implements OnInit {
  isLoading = true;
  hasError = false;
  errorMessage =
    "An unexpected error occurred during authentication. Please try again.";
  newUserError = false;

 
  constructor(
    private keycloakService: KeycloakService,
    private router: Router,
    private helperService: HelperService,
    private apiRequestService: APIRequestService
  ) {}

  ngOnInit(): void {
    console.log("🔄 [CallbackComponent] ngOnInit started.");
    console.log("🔄 [CallbackComponent] Current URL:", window.location.href);
    console.log("🔄 [CallbackComponent] Checking for saved URL...");

    const savedUrl = this.helperService.originalUrl.get();
    console.log("🔄 [CallbackComponent] Found saved URL:", savedUrl);

    this.initiateAuthentication();
  }

  initiateAuthentication(): void {
    this.isLoading = true;
    this.hasError = false;

    const isLoggedIn = this.keycloakService.isLoggedIn();
    console.log(`Callback Component: isLoggedIn status: ${isLoggedIn}`);

    if (isLoggedIn) {
      try {
        console.log(
          "Callback Component: User is logged in. Proceeding with token exchange."
        );
        this.exchangeTokenForProfile();
      } catch (error) {
        console.error(
          "Callback Component: A critical client-side error occurred before API call.",
          error
        );
        this.handleError(
          "A client-side error occurred. Please try returning to the login page."
        );
      }
    } else {
      console.warn(
        "Callback Component: User is not logged in, cannot proceed."
      );
      this.handleError(
        "Your session could not be verified. Please return to the login page and try again."
      );
    }
  }

   setNewUserError(value: boolean): void {
    this.newUserError = value;
    this.isLoading = false;
    this.hasError = false;
    this.errorMessage =
      "It looks like this is your first time logging in. Please register as a customer to continue.";
  }

  private exchangeTokenForProfile(): void {
    // Step B: Store Token
    const accessToken = this.keycloakService.getToken();
    this.helperService.token.set(accessToken);
    this.helperService.keycloakSession.set(true);
    console.log(
      "Callback Component: Keycloak token and session flag stored in localStorage."
    );

    // Step C: Make the API Call
    console.log(
      "Callback Component: Preparing to call gateway for user profile."
    );
    const urlModel = this.helperService.urlFactory(
      APIURLTypes.IAM,
      "api/user/keycloak-profile"
    );

    this.apiRequestService
      .get("", null, urlModel)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(
        (response) => {
          console.log('%c[SUCCESS] Keycloak Profile API Response:', 'color: green; font-weight: bold;', response);
          this.handleSuccessfulLogin(response);
        },
        (error) => {
          console.error(
            "%c[ERROR] Keycloak Profile API Call Failed:",
            "color: red; font-weight: bold;",
            error
          );
          if (
           error && error.error &&
            error.error.message === "USERNR01"
          ) {
            this.setNewUserError(true);
            return;
          }
          const message =
            error?.error?.message ||
            "The authentication service is currently unavailable.";
          this.handleError(message);
        }
      );
  }

  private handleSuccessfulLogin(response: any): void {
    console.log(
      "Callback Component: Populating localStorage with profile data."
    );

    this.helperService.applyProfileResponse(response);

    console.log(
      "Callback Component: Data population complete. Executing routing logic..."
    );
    this.routeUser(response);
  }

  private routeUser(response: any): void {
    console.log(
      "🔄 [CallbackComponent] routeUser called with response:",
      response
    );

    // Check if we have a saved original URL to redirect to
    const originalUrl = this.helperService.originalUrl.get();
    console.log("🔄 [CallbackComponent] Checking for saved URL...");
    console.log("🔄 [CallbackComponent] Found saved URL:", originalUrl);

    if (originalUrl) {
      console.log(
        "🔄 [CallbackComponent] ✅ FOUND saved original URL, redirecting to:",
        originalUrl
      );

      // Clean up after use
      this.helperService.originalUrl.remove();
      console.log(
        "🔄 [CallbackComponent] ✅ Cleaned up saved URL from localStorage"
      );

      // Verify cleanup
      const verifyCleanup = this.helperService.originalUrl.get();
      console.log(
        "🔄 [CallbackComponent] ✅ Verified cleanup (should be null):",
        verifyCleanup
      );

      console.log(
        "🔄 [CallbackComponent] ✅ Navigating to original URL:",
        originalUrl
      );
      this.router.navigateByUrl(originalUrl);
      return;
    }

    // --- Fallback to original routing logic if no saved URL ---
    console.log(
      "🔄 [CallbackComponent] ❌ No saved URL found, using default routing logic."
    );
    console.log(
      "🔄 [CallbackComponent] User type:",
      response.user?.customer_type
    );
    console.log(
      "🔄 [CallbackComponent] Customer ID:",
      response.user?.customer_id
    );
    console.log(
      "🔄 [CallbackComponent] Customer name:",
      response.user?.customer_name
    );
    console.log("🔄 [CallbackComponent] Primary view:", response.primary_view);

    if (response.user?.customer_type === "SERVICE_PROVIDER") {
      this.helperService.setAgnextAdmin(true);
      console.log(
        "🔄 [CallbackComponent] SERVICE_PROVIDER user, navigating to superadmin/customers."
      );
      this.router.navigate(["superadmin/customers"]);
    } else if (
      response.user?.customer_type === "CUSTOMER" &&
      (response.user.customer_id === 212 ||
        response.user.customer_name === "MCS")
    ) {
      console.log(
        "🔄 [CallbackComponent] Specific CUSTOMER user, navigating to dashboard/landing."
      );
      this.router.navigate(["dashboard/landing"]);
    } else {
      // Fixed: Match exact legacy flow logic structure
      if (response.primary_view && response.primary_view === "TABLEU") {
        console.log(
          "🔄 [CallbackComponent] User with TABLEU view, navigating to dashboard/graphic."
        );
        this.router.navigate(["dashboard/graphic"]);
      }
      if (
        response.primary_view &&
        this.helperService.isPermitted("shade_card_view")
      ) {
        console.log(
          "🔄 [CallbackComponent] User with shade_card_view permission, navigating to visio/shadecard."
        );
        this.router.navigate(["visio/shadecard"]);
      } else {
        console.log(
          "🔄 [CallbackComponent] Default user, navigating to dashboard/landing."
        );
        this.router.navigate(["dashboard/landing"]);
      }

      this.helperService.setAgnextAdmin(false);
    }
  }

  private handleError(message: string): void {
    this.isLoading = false;
    this.hasError = true;
    this.errorMessage = message;
  }

  retry(): void {
    console.log("User initiated retry...");
    this.initiateAuthentication();
  }

  returnToLogin(): void {
    console.log("User initiated return to login...");
    this.keycloakService.logout();
  }

  registerAsCustomer(): void {
    console.log("User initiated registration as customer...");
    this.router.navigate(["/auth/register-customer"]);
  }
}
